/* eslint-disable indent */
import {
  BRIDGE_METHODS,
  BRIDGE_NAME,
} from "./bridgeConstants";
import { getUrlConfig } from "./deeplinkUrlConfig";
import ChatBotMeta from "../utils/singletonChatBotMeta";
import {
  encodeToBase64,
  getValueForQueryParam,
} from "../utils/helperFunctions";

export const TOKEN_DELIMITER = "###";
export const TXN_HISTORY = "txnHistory";

export const IRIS_TOKEN = "iris_token";

export const validateAndGetToken = authToken => {
  try {
    if (!authToken) {
      return null;
    }

    var tokenResponse = authToken.split(TOKEN_DELIMITER);
    var currentTimeStamp = Date.now();
    var expiry =
      new Date(parseInt(tokenResponse[1])) * 1000;

    var timeDifference = currentTimeStamp - expiry;

    return timeDifference <= -600000
      ? {
          //Need this to add buffer time before expiry
          token: tokenResponse[0],
          userId: tokenResponse[2],
          name: tokenResponse[3],
          language: tokenResponse[4],
        }
      : null;
  } catch (err) {
    console.log(err);
    return null;
  }
};
export const generateToken = () => {
  try {
    var context = window.btoa(
      JSON.stringify({
        bridgeType: BRIDGE_METHODS.GENERATE_TOKEN,
      })
    );
    var data = "";
    Freshbot.getToken(context, data, BRIDGE_NAME);
  } catch (err) {
    console.log(err);
  }
};

// Fetches App Info from the main sb app and attaches it to the window object of faq iframe via post message
export const fetchAppInfo = () => {
  try {
    var context = window.btoa(
      JSON.stringify({
        bridgeType: BRIDGE_METHODS.FETCH_APP_INFO,
      })
    );
    var data = "";
    NativeStore.getAppInfo(context, data);
  } catch (err) {
    console.log(err);
  }
};
export const getTokenStamped = function (
  rawToken,
  expiresAt,
  userId,
  userName,
  language
) {
  return rawToken.concat(
    TOKEN_DELIMITER,
    expiresAt,
    TOKEN_DELIMITER,
    userId,
    TOKEN_DELIMITER,
    userName,
    TOKEN_DELIMITER,
    language
  );
};

/**
 * @desc    Save the token inside the sdk.
 *
 * @param token     {String}    The token that ought to be saved
 */
export const saveToken = token => {
  try {
    NativeStore.setString(IRIS_TOKEN, token);
  } catch (err) {
    console.log(err);
  }
};

export const isWebView = () => {
  return isAndroidWebView() || isIosWebView();
};

export const isAndroidWebView = () => {
  const userAgent = (
    navigator.userAgent ||
    navigator.vendor ||
    window.opera
  ).toLowerCase();
  const { standalone } = window.navigator;
  return (
    standalone ||
    userAgent.includes("wv") ||
    userAgent.includes("webview")
  );
};

export const isIosWebView = () => {
  const userAgent =
    navigator.userAgent || navigator.vendor || window.opera;

  const isIOS =
    /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;

  // Crios and Chrome - for Chrome app GSA - for Google search app;
  const isChromeBrowser =
    /CriOS/.test(userAgent) ||
    /Chrome/.test(userAgent) ||
    /GSA/.test(userAgent);

  const isWebView =
    (userAgent.includes("AppleWebKit") &&
      !userAgent.includes("Safari")) ||
    (window.webkit && window.webkit.messageHandlers);

  return !isChromeBrowser && isIOS && isWebView;
};

export const getMobileOperatingSystem = () => {
  if (typeof window === "undefined") {
    return null;
  }
  var userAgent =
    navigator.userAgent ||
    navigator.vendor ||
    navigator.standalone ||
    window.opera;
  if (/android/i.test(userAgent)) {
    return "ANDROID";
  }
  if (
    /iPad|iPhone|iPod|safari/.test(userAgent) &&
    !window.MSStream
  ) {
    return "IOS";
  }
};

export const isMobile = () => {
  return ["ANDROID", "IOS"].includes(
    getMobileOperatingSystem()
  );
};

export const isWeb = () => {
  if (typeof window === "undefined") {
    return false;
  }
  return !isMobile();
};
/**
 * @desc    To fetch token from the data store of Native layer.
 *
 *          The callback of it will define what should be the next course of action.
 *          If token is null of expired, generate token flow will be followed.
 */
export const fetchToken = function () {
  try {
    var context = btoa(
      JSON.stringify({
        bridgeType: BRIDGE_METHODS.FETCH_TOKEN,
      })
    );
    var data = btoa(
      JSON.stringify({
        key: IRIS_TOKEN,
      })
    );
    NativeStore.getString(context, data, BRIDGE_NAME);
  } catch (err) {
    console.log(err);
  }
};
export const isMobileBridgePresent = () => {
  return (
    !!window?.JsHandler || !!window?.webkit?.messageHandlers
  );
};

export const triggerNavigation = ({ deepLink, webURL }) => {
  const OS = getMobileOperatingSystem()?.toUpperCase();
  if (OS === "ANDROID") {
    try {
      window.JsHandler.navigateDeeplink(deepLink, false);
    } catch (e) {
      webURL && window.open(webURL);
    }
  } else if (OS === "IOS") {
    try {
      window.webkit.messageHandlers.navigateTo.postMessage({
        data: btoa(JSON.stringify({ dl: deepLink })),
      });
    } catch (err) {
      console.log("Error opening app screen: ", err);
    }
  } else {
    webURL && window.open(webURL);
  }
};

export const navigateToURL = ctaKey => {
  const config = getUrlConfig(ChatBotMeta.get())?.[ctaKey];
  const OS = getMobileOperatingSystem();
  if (config) {
    const { deepLink, webURL } = config;
    if (
      ctaKey === "CHATBOT" &&
      ["ANDROID", "IOS"].includes(OS)
    ) {
      window.open(deepLink);
      return;
    }
    triggerNavigation({ deepLink, webURL });
  }
};

export const openURL = (
  url,
  title = "",
  actionType = ""
) => {
  try {
    var data = btoa(
      JSON.stringify({
        url,
        title: title || "Ticket Details",
        isExternal: true,
        actionType,
      })
    );
    Freshbot.openURL(data);
  } catch (err) {
    console.log(err);
    window.open(url); // This is to open Grievances URL on Mobile Browser
  }
};

export const ingestData = data => {
  let stringifiedData = JSON.stringify({
    ...ChatBotMeta.get(),
    ...data?.data,
    // Passing src from query params since src gets changed to FAQ in chatbot meta for local faq specific chatbot
    src: getValueForQueryParam("src"),
  });

  // sanitize string before encoding
  const encodedString = encodeToBase64(stringifiedData);

  try {
    Freshbot.logEvent(data?.name, "HELP", encodedString);
  } catch (err) {
    console.log(err);
  }
};

export const onBackPressed = () => {
  try {
    Freshbot.onBackPressed();
  } catch (err) {
    console.log(err);
  }
};

//TODO: Remove exitFromWebView when the adoption is 100%

export const exitFromWebView = () => {
  try {
    Freshbot.exitWebview();
  } catch (err) {
    console.log(err);
  }
};
